import { useEffect, useState } from 'react';
import { Button, Label, Loader, Message, Modal, Table } from 'semantic-ui-react';
import { QRCodeSVG } from 'qrcode.react';
import { usersService } from '../../services';
import { InternetUserSummary, MfaSetupResponse } from '../../services/UsersService/types';

const MfaManagement = () => {
    const [users, setUsers] = useState<InternetUserSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [mfaSetup, setMfaSetup] = useState<MfaSetupResponse | null>(null);
    const [setupUsername, setSetupUsername] = useState<string>('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await usersService.getAll();
            if (res.success) {
                setUsers(res.result);
            } else {
                setError(res.message || 'Failed to load users.');
            }
        } catch {
            setError('Failed to load users.');
        } finally {
            setLoading(false);
        }
    };

    const handleEnable = async (user: InternetUserSummary) => {
        setActionLoading(user.id);
        try {
            const res = await usersService.enableMfa(user.id);
            if (res.success) {
                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, mfaEnabled: true } : u));
                setSetupUsername(user.username || user.name);
                setMfaSetup(res.result);
            } else {
                setError(res.message || 'Failed to enable MFA.');
            }
        } catch {
            setError('Failed to enable MFA.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDisable = async (user: InternetUserSummary) => {
        setActionLoading(user.id);
        try {
            const res = await usersService.disableMfa(user.id);
            if (res.success) {
                setUsers(prev => prev.map(u => u.id === user.id ? { ...u, mfaEnabled: false } : u));
            } else {
                setError(res.message || 'Failed to disable MFA.');
            }
        } catch {
            setError('Failed to disable MFA.');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                <Loader active inline='centered' size='large'>Loading users...</Loader>
            </div>
        );
    }

    return (
        <div className='fg-admin-section'>
            <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>MFA Management</h3>

            {error && (
                <Message negative onDismiss={() => setError(null)}>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <Table celled striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Username</Table.HeaderCell>
                        <Table.HeaderCell>Email</Table.HeaderCell>
                        <Table.HeaderCell textAlign='center'>MFA Status</Table.HeaderCell>
                        <Table.HeaderCell textAlign='center'>Action</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {users.map(user => (
                        <Table.Row key={user.id}>
                            <Table.Cell>{user.name}</Table.Cell>
                            <Table.Cell>{user.username}</Table.Cell>
                            <Table.Cell>{user.email}</Table.Cell>
                            <Table.Cell textAlign='center'>
                                {user.mfaEnabled
                                    ? <Label color='green'>Enabled</Label>
                                    : <Label color='grey'>Disabled</Label>
                                }
                            </Table.Cell>
                            <Table.Cell textAlign='center'>
                                {user.mfaEnabled ? (
                                    <Button
                                        size='small'
                                        color='red'
                                        loading={actionLoading === user.id}
                                        disabled={actionLoading !== null}
                                        onClick={() => handleDisable(user)}
                                    >
                                        Disable MFA
                                    </Button>
                                ) : (
                                    <Button
                                        size='small'
                                        color='blue'
                                        loading={actionLoading === user.id}
                                        disabled={actionLoading !== null}
                                        onClick={() => handleEnable(user)}
                                    >
                                        Enable MFA
                                    </Button>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                    {users.length === 0 && (
                        <Table.Row>
                            <Table.Cell colSpan={5} textAlign='center' style={{ color: '#888' }}>
                                No users found.
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            <Modal
                open={mfaSetup !== null}
                onClose={() => setMfaSetup(null)}
                size='small'
                closeIcon
            >
                <Modal.Header>MFA Enabled for {setupUsername}</Modal.Header>
                <Modal.Content style={{ textAlign: 'center' }}>
                    <p style={{ marginBottom: '1rem', color: '#555' }}>
                        Have the user scan the QR code below with their authenticator app (e.g. Google Authenticator, Authy).
                    </p>
                    {mfaSetup?.qrCodeUri && (
                        <div style={{ display: 'inline-block', padding: '16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '1.5rem' }}>
                            <QRCodeSVG value={mfaSetup.qrCodeUri} size={220} />
                        </div>
                    )}
                    <div style={{ background: '#f8f8f8', borderRadius: '6px', padding: '12px', marginBottom: '1rem' }}>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                            Manual entry key (if QR code cannot be scanned):
                        </p>
                        <code style={{ fontSize: '1rem', letterSpacing: '2px', color: '#222', wordBreak: 'break-all' }}>
                            {mfaSetup?.secret}
                        </code>
                    </div>
                    <p style={{ color: '#888', fontSize: '0.8rem' }}>
                        This secret will not be shown again. Share it securely with the user.
                    </p>
                </Modal.Content>
                <Modal.Actions>
                    <Button color='blue' onClick={() => setMfaSetup(null)}>Done</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default MfaManagement;
